import Link from 'next/link';
import Image from 'next/image';
import { getDotcmsImageURL } from '@/app/utils/getDotcmsImageURL';
import { ImagePlacerholder } from '@/components/shared/dotCardImage';
import { formatDate } from '@/app/utils/formatDate';

function NewsCard({ post }) {
    const href = post.URL_MAP_FOR_CONTENT || `/news/${post.urlTitle}`;
    const imageSrc = post.image || post.titleImage?.idPath || post.titleImage?.versionPath;
    const imageUrl = imageSrc ? getDotcmsImageURL({ src: imageSrc, width: 600 }) : null;
    const date = formatDate(post.publishDate, 'short');

    return (
        <li className="group relative flex flex-col overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm transition-shadow duration-200 hover:shadow-lg">
            <Link href={href} className="absolute inset-0 z-10" aria-label={post.title} />

            <div className="relative aspect-video w-full overflow-hidden">
                {imageUrl ? (
                    <Image
                        src={imageUrl}
                        alt={post.thumbnailAlt || post.title}
                        fill
                        className="object-cover transition-transform duration-300 group-hover:scale-105"
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                    />
                ) : (
                    <ImagePlacerholder />
                )}
            </div>

            <div className="flex flex-1 flex-col gap-2 p-5">
                {date && (
                    <time className="text-xs font-bold uppercase tracking-widest text-fuschia-700">
                        {date}
                    </time>
                )}
                <h3 className="line-clamp-3 text-base font-bold leading-snug text-blue-700 transition-colors duration-200 group-hover:text-fuschia-700">
                    {post.title}
                </h3>
                {post.teaser && (
                    <p className="line-clamp-2 text-sm leading-relaxed text-blue-600/80">
                        {post.teaser}
                    </p>
                )}
            </div>
        </li>
    );
}

const GRID_CLASSES = {
    1: 'grid grid-cols-1 gap-6',
    2: 'grid grid-cols-1 gap-6 sm:grid-cols-2',
    3: 'grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3'
};

export function NewsRelatedPosts({ posts = [], maxLimit = 4 }) {
    if (!posts.length) return null;

    const limited = posts.slice(0, maxLimit);
    const gridClass =
        GRID_CLASSES[limited.length] ??
        'grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4';

    return (
        <div className="p-4 pt-11 md:p-16 lg:px-32">
            <div className="container p-0">
                <div className="mb-12 flex flex-col gap-8 lg:mb-24">
                    <p className="text-2xl font-bold leading-tight">Recommended Reading</p>
                    <ul className={gridClass}>
                        {limited.map((post, i) => (
                            <NewsCard key={post.identifier || post.urlTitle || i} post={post} />
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

export function NewsRelatedPostsSkeleton() {
    return (
        <div className="p-4 pt-11 md:p-16 lg:px-32">
            <div className="container p-0">
                <div className="mb-12 flex flex-col gap-8 lg:mb-24">
                    <p className="text-2xl font-bold leading-tight">Recommended Reading</p>
                    <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <li
                                key={i}
                                className="animate-pulse overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm">
                                <div className="aspect-video w-full bg-blue-100" />
                                <div className="flex flex-col gap-3 p-5">
                                    <div className="h-3 w-20 rounded-full bg-blue-100" />
                                    <div className="h-4 w-full rounded-full bg-blue-100" />
                                    <div className="h-4 w-4/5 rounded-full bg-blue-100" />
                                    <div className="h-3 w-full rounded-full bg-blue-50" />
                                    <div className="h-3 w-3/4 rounded-full bg-blue-50" />
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}
